import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon, TicketIcon, BookOpenIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { ticketService, articleService } from '../../services';

/* ─── tiny debounce hook ─────────────────────────────────────────────────── */
const useDebounce = (value, delay = 300) => {
  const [dv, setDv] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return dv;
};

/* ─── SearchModal ────────────────────────────────────────────────────────── */
const SearchModal = ({ open, onClose }) => {
  const [q, setQ]             = useState('');
  const [tickets, setTickets] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const dq = useDebounce(q, 280);

  /* focus input whenever modal opens */
  useEffect(() => {
    if (open) {
      setQ('');
      setTickets([]);
      setArticles([]);
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  /* search when debounced query changes */
  useEffect(() => {
    if (!dq || dq.length < 2) {
      setTickets([]);
      setArticles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [tr, ar] = await Promise.allSettled([
          ticketService.getAll({ search: dq, limit: 6 }),
          articleService.getAll({ search: dq, limit: 5, status: 'published' }),
        ]);
        if (cancelled) return;
        setTickets(tr.status === 'fulfilled' ? (tr.value.data?.data || []) : []);
        setArticles(ar.status === 'fulfilled' ? (ar.value.data?.data || []) : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dq]);

  const allItems = [
    ...tickets.map((t) => ({
      id: t._id,
      label: t.subject,
      meta: `#${t.ticketNumber} · ${t.status?.replace(/_/g, ' ')}`,
      path: `/tickets/${t._id}`,
      icon: TicketIcon,
      color: 'text-[#12206b]',
      bg: 'bg-[#e8f7fc]',
    })),
    ...articles.map((a) => ({
      id: a._id,
      label: a.title,
      meta: a.category || 'Article',
      path: `/knowledge-base/${a.slug || a._id}`,
      icon: BookOpenIcon,
      color: 'text-[#00b4d8]',
      bg: 'bg-[#e8f7fc]',
    })),
  ];

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, allItems.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && allItems[activeIdx]) {
      navigate(allItems[activeIdx].path);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0" style={{ background: 'rgba(10,16,32,0.45)', backdropFilter: 'blur(4px)' }} />

      {/* Panel */}
      <div
        className="relative w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl animate-scale-in"
        style={{ background: '#fff', border: '1px solid rgba(18,32,107,0.18)', boxShadow: '0 20px 60px rgba(10,16,32,0.25), 0 0 0 1px rgba(18,32,107,0.12)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input row */}
        <div className="flex items-center gap-3 px-4 py-3.5" style={{ borderBottom: '1px solid #f0f1f5' }}>
          <MagnifyingGlassIcon className="w-5 h-5 text-[#00b4d8] flex-shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setActiveIdx(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Search tickets, articles…"
            className="flex-1 text-sm text-gray-900 placeholder:text-gray-400 outline-none bg-transparent"
          />
          {loading && (
            <span className="w-4 h-4 border-2 border-[#90e0f3] border-t-[#12206b] rounded-full animate-spin flex-shrink-0" />
          )}
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0">
            <XMarkIcon className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Results */}
        {allItems.length > 0 && (
          <ul className="py-1.5 max-h-[340px] overflow-y-auto">
            {tickets.length > 0 && (
              <>
                <li className="px-4 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Tickets</li>
                {tickets.map((t, i) => {
                  const item = allItems[i];
                  const Icon = item.icon;
                  return (
                    <li key={t._id}>
                      <button
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${activeIdx === i ? 'bg-[#e8f7fc]' : 'hover:bg-gray-50'}`}
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => { navigate(item.path); onClose(); }}
                      >
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${item.bg}`}>
                          <Icon className={`w-4 h-4 ${item.color}`} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-gray-900 truncate">{item.label}</p>
                          <p className="text-[11px] text-gray-400 capitalize">{item.meta}</p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </>
            )}
            {articles.length > 0 && (
              <>
                <li className="px-4 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  {tickets.length > 0 && <span className="block mt-1" />}
                  Knowledge Base
                </li>
                {articles.map((a, i) => {
                  const idx = tickets.length + i;
                  const item = allItems[idx];
                  const Icon = item.icon;
                  return (
                    <li key={a._id}>
                      <button
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${activeIdx === idx ? 'bg-[#e8f7fc]' : 'hover:bg-gray-50'}`}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => { navigate(item.path); onClose(); }}
                      >
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${item.bg}`}>
                          <Icon className={`w-4 h-4 ${item.color}`} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-gray-900 truncate">{item.label}</p>
                          <p className="text-[11px] text-gray-400">{item.meta}</p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </>
            )}
          </ul>
        )}

        {/* Empty state */}
        {q.length >= 2 && !loading && allItems.length === 0 && (
          <div className="px-4 py-10 text-center">
            <MagnifyingGlassIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No results for <span className="font-medium text-gray-600">"{q}"</span></p>
          </div>
        )}

        {/* Hint bar */}
        <div
          className="px-4 py-2.5 flex items-center gap-4 text-[11px] text-gray-400"
          style={{ borderTop: '1px solid #f0f1f5' }}
        >
          <span><kbd className="inline-block px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono leading-tight">↑↓</kbd> navigate</span>
          <span><kbd className="inline-block px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono leading-tight">↵</kbd> open</span>
          <span><kbd className="inline-block px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono leading-tight">Esc</kbd> close</span>
          <span className="ml-auto"><kbd className="inline-block px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono leading-tight">/</kbd> to open</span>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
